export class TextureUtils {
  /**
   * Binds a given WebGL texture to texture unit 0 and sets the corresponding sampler uniform in the shader program.
   *
   * @param gl - The WebGL2RenderingContext to use for binding.
   * @param program - The WebGLProgram to bind the texture to.
   * @param tex - The WebGLTexture to bind.
   * @param key - The name of the sampler uniform orthe sampler location in the shader program to associate with the texture.
   * @param unit - The texture unit to bind the texture to (0-15 for WebGL2).
   *
   * @remarks
   * If the specified uniform cannot be found in the shader program, a warning is logged to the console.
   */
    static bindTex(
    gl: WebGL2RenderingContext,
    program: WebGLProgram,
    tex: WebGLTexture | null | undefined,
    key: string | WebGLUniformLocation,
    unit: number,
    target: number = gl.TEXTURE_2D
  ) {
    if (!tex) {
      console.warn(`[TextureUtils] Attempted to bind null/undefined texture to sampler "${key}" at texture unit ${unit}`);
      return;
    }
    var loc : WebGLUniformLocation | null;
    if(typeof key === "string"){
      loc = gl.getUniformLocation(program, key);
    }else{
      loc = key;
    }
    if (loc === null) {
      console.warn(`[TextureUtils] Cannot find sampler uniform "${key}" in shader program`);
      return;
    }
    // Bind to the specified texture unit
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(target, tex);
    // Tell the shader's sampler to use this texture unit
    gl.uniform1i(loc, unit);
  }


  /**
   * Uploads a Float32Array to GPU as a 2D RGBA32F texture.
   * Each texel stores 4 floats (R, G, B, A).
   * (totally not vibecoded)
   * @param gl         - WebGL2RenderingContext
   * @param data       - Float32Array, Float16Array, or Int8Array containing your raw data
   * @param widthHint  - Optional: manual texture width (default auto-calculated)
   * @returns texture: WebGLTexture
   */
  static packFloatArrayToTexture(
    gl: WebGL2RenderingContext,
    data: Float32Array | Float16Array | Int8Array,
    widthHint?: number
  ) {
    if (data.length % 4 !== 0) {
      console.warn(
        `[packFloatArrayToTexture] Padding input from ${data.length} to multiple of 4`
      );
      const padded = new Float32Array(Math.ceil(data.length / 4) * 4);
      padded.set(data);
      data = padded;
    }

    const totalTexels = data.length / 4;

    const width = widthHint || Math.ceil(Math.sqrt(totalTexels));
    const height = Math.ceil(totalTexels / width);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);

    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      (data instanceof Float32Array) ? gl.RGBA32F: ((data instanceof Float16Array) ? gl.RGBA16F : gl.RGBA8_SNORM), // Internal format
      width,
      height,
      0,
      gl.RGBA, // Format of incoming data
      (data instanceof Int8Array) ? gl.BYTE:gl.FLOAT,
      new Float32Array(width * height * 4).fill(0).map((_, i) => data[i] ?? 0) // Fill/pad if needed
    );

    // NEAREST = no filtering/interpolation
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    return texture;
  }
  /**
   * Creates and initializes a WebGL 2D texture with the specified parameters.
   *
   * @param gl - The WebGL2 rendering context.
   * @param width - The width of the texture in pixels.
   * @param height - The height of the texture in pixels.
   * @param internalFormat - The internal format of the texture (e.g., `gl.RGBA8`).
   * @param format - The format of the pixel data (e.g., `gl.RGBA`).
   * @param type - The data type of the pixel data (e.g., `gl.UNSIGNED_BYTE`).
   * @param data - Optional. The pixel data to initialize the texture with. If `null`, the texture is initialized with empty data.
   * @returns The created WebGLTexture object.
   */
  static createTexture2D(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    internalFormat: number,
    format: number,
    type: number,
    data: ArrayBufferView | null = null,
    minFilter: number = gl.NEAREST,
    magFilter: number = gl.NEAREST,
    wrapS: number = gl.CLAMP_TO_EDGE,
    wrapT: number = gl.CLAMP_TO_EDGE
  ) {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      internalFormat,
      width,
      height,
      0,
      format,
      type,
      data
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
  }
  /**
   * Creates and initializes a WebGL 2D texture with the specified parameters.
   *
   * @param gl - The WebGL2 rendering context.
   * @param width - The width of the texture in pixels.
   * @param height - The height of the texture in pixels.
   * @param internalFormat - The internal format of the texture (e.g., `gl.RGBA8`).
   * @param format - The format of the pixel data (e.g., `gl.RGBA`).
   * @param type - The data type of the pixel data (e.g., `gl.UNSIGNED_BYTE`).
   * @param data - Optional. The pixel data to initialize the texture with. If `null`, the texture is initialized with empty data.
   * @returns The created WebGLTexture object.
   */
  static createTexture3D(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    depth: number,
    internalFormat: number,
    format: number,
    type: number,
    data: ArrayBufferView | null = null,
    minFilter: number = gl.NEAREST,
    magFilter: number = gl.NEAREST,
    wrapS: number = gl.CLAMP_TO_EDGE,
    wrapT: number = gl.CLAMP_TO_EDGE,
    wrapR: number = gl.CLAMP_TO_EDGE
  ) {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_3D, texture);
    gl.texImage3D(
      gl.TEXTURE_3D,
      0,
      internalFormat,
      width,
      height,
      depth,
      0,
      format,
      type,
      data
    );
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, minFilter);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, magFilter);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, wrapS);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, wrapT);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, wrapR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, wrapR);
    gl.bindTexture(gl.TEXTURE_3D, null);
    return texture;
  }

  /**
   * Creates and initializes a WebGL 2D array texture with the specified parameters.
   *
   * @param gl - The WebGL2 rendering context.
   * @param width - The width of each layer in pixels.
   * @param height - The height of each layer in pixels.
   * @param layers - The number of layers in the array.
   * @param internalFormat - The internal format of the texture (e.g., `gl.DEPTH_COMPONENT32F`).
   * @param format - The format of the pixel data (e.g., `gl.DEPTH_COMPONENT`).
   * @param type - The data type of the pixel data (e.g., `gl.FLOAT`).
   * @param data - Optional. The pixel data to initialize the texture with. If `null`, the texture is initialized with empty data.
   * @param minFilter - The minification filter (default: gl.NEAREST).
   * @param magFilter - The magnification filter (default: gl.NEAREST).
   * @param wrapS - The wrap mode for S coordinate (default: gl.CLAMP_TO_EDGE).
   * @param wrapT - The wrap mode for T coordinate (default: gl.CLAMP_TO_EDGE).
   * @returns The created WebGLTexture object.
   */
  static createTexture2DArray(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    layers: number,
    internalFormat: number,
    format: number,
    type: number,
    data: ArrayBufferView | null = null,
    minFilter: number = gl.NEAREST,
    magFilter: number = gl.NEAREST,
    wrapS: number = gl.CLAMP_TO_EDGE,
    wrapT: number = gl.CLAMP_TO_EDGE
  ) {
    const texture = gl.createTexture();
    if (!texture) {
      throw new Error("Failed to create texture");
    }
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
    gl.texImage3D(
      gl.TEXTURE_2D_ARRAY,
      0,
      internalFormat,
      width,
      height,
      layers,
      0,
      format,
      type,
      data
    );
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, minFilter);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, magFilter);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, wrapS);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, wrapT);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
    return texture;
  }
}
